import React, { useState, useEffect } from 'react';
import { X, CheckCircle, Loader } from 'lucide-react';
import { Document, Goal, Task, db } from '../../lib/db';
import { toast } from 'sonner';

interface CompleteTaskModalProps {
  document: Document;
  dbUserId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function CompleteTaskModal({ document, dbUserId, onClose, onSuccess }: CompleteTaskModalProps) {
  const [loading, setLoading] = useState(false);
  const [deliverables, setDeliverables] = useState<Goal[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedDeliverableId, setSelectedDeliverableId] = useState<string>('');
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');
  const [completionNotes, setCompletionNotes] = useState('');

  useEffect(() => {
    loadDeliverables();
  }, []);

  useEffect(() => {
    if (selectedDeliverableId) {
      loadTasks(selectedDeliverableId);
    } else {
      setTasks([]);
      setSelectedTaskId('');
    }
  }, [selectedDeliverableId]);

  const loadDeliverables = async () => {
    try {
      const goals = await db.goals.findByUserId(dbUserId);
      setDeliverables(goals.filter(g => !g.is_archived));
    } catch (error) {
      console.error('Error loading deliverables:', error);
      toast.error('Failed to load deliverables');
    }
  };

  const loadTasks = async (goalId: string) => {
    try {
      const tasksList = await db.tasks.findByGoalId(goalId, dbUserId);
      // Only show incomplete tasks
      setTasks(tasksList.filter(t => !t.is_completed));
    } catch (error) {
      console.error('Error loading tasks:', error);
      toast.error('Failed to load tasks');
    }
  };

  const handleSubmit = async () => {
    if (!selectedTaskId) {
      toast.error('Please select a task to complete');
      return;
    }

    setLoading(true);
    try {
      const task = tasks.find(t => t.id === selectedTaskId);
      if (!task) return;

      // Mark the task as complete
      await db.tasks.update(selectedTaskId, {
        is_completed: true,
        completed_date: new Date()
      });

      // Record the action
      await db.documentActions.create({
        document_id: document.id,
        user_id: dbUserId,
        action_type: 'complete_task',
        action_data: { 
          task_id: selectedTaskId,
          deliverable_id: selectedDeliverableId,
          task_title: task.title,
          completion_notes: completionNotes
        }
      });

      toast.success('Task marked as complete');
      onSuccess();
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Mark Task as Complete</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Deliverable *
                </label>
                <select
                  value={selectedDeliverableId}
                  onChange={(e) => setSelectedDeliverableId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select a deliverable...</option>
                  {deliverables.map(deliverable => (
                    <option key={deliverable.id} value={deliverable.id}>
                      {deliverable.title} ({deliverable.year})
                    </option>
                  ))}
                </select>
              </div>

              {selectedDeliverableId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Task to Complete *
                  </label>
                  {tasks.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No incomplete tasks in this deliverable</p>
                  ) : (
                    <select
                      value={selectedTaskId}
                      onChange={(e) => setSelectedTaskId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select a task...</option>
                      {tasks.map(task => (
                        <option key={task.id} value={task.id}>
                          {task.title}
                          {task.due_date && ` (Due: ${new Date(task.due_date).toLocaleDateString()})`}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {selectedTaskId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Completion Notes (Optional)
                  </label>
                  <textarea
                    value={completionNotes}
                    onChange={(e) => setCompletionNotes(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Add any notes about completing this task..."
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || !selectedTaskId}
                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin inline mr-2" />
                    Completing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 inline mr-2" />
                    Mark Complete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}